import { MutableRefObject, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Option, isNone, isSome, none } from 'fp-ts/lib/Option';

import { ErrorData } from 'domain/types';
import Branding from 'domain/entity/branding';
import { ConstraintsPerLayoutElement, LayoutElementType, LayoutType } from 'domain/entity/layout';
import { ReadWidgetData } from 'use-case/widgets/data/read/readWidgetDataUseCase';
import { CriteriaCallback, useCase } from 'adapter/in/common/hooks/useCase';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import { useBranding } from 'adapter/in/common/context/static/Branding';
import { useIntersect } from 'adapter/in/common/hooks/useIntersect';
import { WidgetCriteria, getWidgetBuilder } from 'adapter/in/common/widget/widgetBuilder';
import { WidgetDataTypes } from 'domain/entity/widgetData';
import { useWidgetDataCriteriaDependencies, useWidgetDataDependencies } from 'adapter/in/common/commonDependencies';
import Dashboard from 'domain/entity/dashboard';
import { getA4FooterText, getA4HeaderText } from 'adapter/in/reports/utils/A4Frame/A4Frame';

export type WidgetDataError = Option<ErrorData>;
export type WidgetDataResult = Option<WidgetDataTypes>;

export const useWidgetData = (
    readWidgetData: ReadWidgetData
): {
    isWidgetDataLoading: boolean;
    widgetData: WidgetDataResult;
    widgetDataError: WidgetDataError;
    readWidgetData: CriteriaCallback;
    reset: () => void;
} => {
    const { isLoading, data, error, execute, reset } = useCase(readWidgetData);

    return {
        isWidgetDataLoading: isLoading,
        widgetData: data,
        widgetDataError: error,
        readWidgetData: execute,
        reset,
    };
};

export const useWidgetBuilder = (
    widgetCriteria: WidgetCriteria,
    dashboardId: string,
    type: string,
    onReadWidgetDataDependencesChange?: (lastTimeUpdated: MutableRefObject<number>) => boolean,
    triggerRefreshTimestamp?: number,
    sizeIssue?: boolean,
    isEditEnabled?: boolean,
    reportTemplateData?: Dashboard
): {
    WidgetBuilder: (
        width: number,
        height: number,
        layoutType?: LayoutType,
        isOnEdit?: boolean,
        sizeIssue?: ConstraintsPerLayoutElement
    ) => ReactElement;
    lastTimeUpdated: MutableRefObject<number>;
    handleReadWidgetData: (frameText: string) => void;
    branding: Branding;
    hasDataError: boolean;
} => {
    const [specificWidgetData, setSpecificWidgetData] = useState<WidgetDataResult>(none);
    const { readWidgetData: readWidgetDataUseCase } = useWidgetDataDependencies();
    const { buildWidgetDataCriteria, registerWidget, triggerWidgetLoads } = useWidgetDataCriteriaDependencies();
    const { profile } = useProfile();
    const { branding } = useBranding();

    const { referencer, entry } = useIntersect({
        root: null,
        threshold: 0.2,
        rootMargin: '0px',
    });
    const [markLoaded, setMarkLoaded] = useState<(() => void) | undefined>();
    const hasLoadedDueToPDF = useRef(false);
    const lastTimeUpdated = useRef(0);
    const { isWidgetDataLoading, widgetData, widgetDataError: hasWidgetDataError, readWidgetData } = useWidgetData(readWidgetDataUseCase);

    useEffect(() => {
        if (!sizeIssue) {
            const deregister = registerWidget?.();
            setMarkLoaded(() => deregister);
        }
    }, [sizeIssue]);

    const handleReadWidgetData = useCallback(
        () => widgetCriteria.id && readWidgetData(buildWidgetDataCriteria(widgetCriteria.id, widgetCriteria.type)),
        [widgetCriteria]
    );

    const handleReadFrameWidgetData = useCallback(
        (frameText: string) => widgetCriteria.id && readWidgetData(buildWidgetDataCriteria(widgetCriteria.id, widgetCriteria.type, frameText)),
        [widgetCriteria]
    );

    useEffect(() => {
        const hasId = !!widgetCriteria.id;
        const shouldForceByPDF = triggerWidgetLoads && !hasLoadedDueToPDF.current;
        const shouldLoadNormally = entry.isIntersecting && (onReadWidgetDataDependencesChange?.(lastTimeUpdated) ?? true);

        if (hasId && (shouldForceByPDF || shouldLoadNormally)) {
            lastTimeUpdated.current = Date.now();
            let frameText = '';
            const isA4Header = type === LayoutElementType.A4_HEADER;
            const isA4Footer = type === LayoutElementType.A4_FOOTER;
            if (isA4Header && reportTemplateData && reportTemplateData.frame) {
                frameText = getA4HeaderText(reportTemplateData.frame.header, reportTemplateData.name, profile);
                handleReadFrameWidgetData(frameText);
            } else if (isA4Footer && reportTemplateData && reportTemplateData.frame) {
                frameText = getA4FooterText(reportTemplateData.frame.footer, widgetCriteria.id);
                handleReadFrameWidgetData(frameText);
            } else {
                handleReadWidgetData();
            }

            if (shouldForceByPDF) {
                hasLoadedDueToPDF.current = true;
            }
        }
    }, [
        widgetCriteria,
        entry,
        triggerRefreshTimestamp,
        triggerWidgetLoads,
        onReadWidgetDataDependencesChange,
        profile,
        reportTemplateData,
        type,
    ]);

    useEffect(() => {
        if (isSome(widgetData)) {
            setSpecificWidgetData(widgetData);
            markLoaded?.();
        }
    }, [widgetData]);

    useEffect(() => {
        if (isSome(hasWidgetDataError)) {
            markLoaded?.();
        }
    }, [hasWidgetDataError]);

    return {
        WidgetBuilder: getWidgetBuilder(
            { id: widgetCriteria.id, type: widgetCriteria.type, optionFields: widgetCriteria.optionFields },
            isSome(specificWidgetData) ? specificWidgetData.value : {},
            isWidgetDataLoading || isNone(specificWidgetData),
            branding,
            profile.decimalSeparator,
            profile.thousandsSeparator,
            profile.dateFormat,
            profile.currencySymbolInLastPosition,
            referencer,
            isSome(hasWidgetDataError) ? hasWidgetDataError.value : undefined,
            dashboardId,
            isEditEnabled
        ),
        lastTimeUpdated,
        handleReadWidgetData,
        hasDataError: !!isSome(hasWidgetDataError),
        branding,
    };
};
